import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "../components/site/Footer";

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy · Elevare Solutions";
    const desc = document.querySelector('meta[name="description"]');
    const prev = desc?.getAttribute("content");
    if (desc) desc.setAttribute("content",
      "Elevare Solutions Privacy Policy — how we collect, use, protect, and share your business information, including Stripe, Emergent, and BBB data handling.");
    window.scrollTo(0, 0);
    return () => { if (desc && prev) desc.setAttribute("content", prev); };
  }, []);

  const updated = "August 4, 2026";

  return (
    <div className="min-h-screen bg-[#ded9cb] text-[#1e3d3a]">
      <header className="border-b border-black/10 bg-[#eae7de]/60 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="privacy-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#a3823f] hover:text-[#1e3d3a] transition-colors duration-300">
            <ArrowLeft className="h-3.5 w-3.5" /> Elevare Solutions
          </Link>
          <span className="text-[10px] uppercase tracking-[0.22em] text-black/40">Updated {updated}</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="privacy-page">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#a3823f]">Legal</p>
        <h1 className="mt-4 font-head text-4xl lg:text-5xl font-medium tracking-tight">Privacy Policy</h1>
        <p className="mt-6 text-sm text-black/60 leading-relaxed">
          Elevare Solutions (&ldquo;Elevare,&rdquo; &ldquo;we,&rdquo; &ldquo;our&rdquo;) respects your
          privacy. This Policy explains what information we collect, how we use it, when we share it,
          and the choices you have.
        </p>

        <Section title="1. Information We Collect">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Account &amp; contact:</strong> name, business name, email, phone, address, role.</li>
            <li><strong>Business data:</strong> revenue, expenses, vendor list, payroll, BBB rating history, uploaded documents.</li>
            <li><strong>Payment:</strong> processed by Stripe. Elevare never sees your full card number, CVC, bank account, or routing number.</li>
            <li><strong>Identity verification:</strong> processed by Stripe Identity. Elevare receives only a &ldquo;verified/unverified&rdquo; result.</li>
            <li><strong>Banking:</strong> connected via Stripe Financial Connections. Elevare stores only bank brand + last 4 digits.</li>
            <li><strong>Usage:</strong> pages visited, features used, and diagnostic activity via standard analytics.</li>
          </ul>
        </Section>

        <Section title="2. How We Use Information">
          <ul className="list-disc pl-5 space-y-1">
            <li>Deliver the Services you request (analyses, plans, reports, communications).</li>
            <li>Process bookings, payments, and refunds (see Terms &sect; 3).</li>
            <li>Improve our AI models, dashboards, and tooling in aggregate/anonymised form.</li>
            <li>Send transactional email (Resend, via Emergent-managed integration).</li>
            <li>Comply with legal, tax, and audit obligations.</li>
          </ul>
        </Section>

        <Section title="3. When We Share Information">
          We share information only with (a) sub-processors necessary to run the Services (Stripe,
          Emergent hosting, Apify for public BBB data, Resend for email); (b) our founders and
          contracted advisors bound by confidentiality; (c) authorities where legally compelled.
          We do not sell personal information.
        </Section>

        <Section title="4. Data Security">
          Client vault documents are encrypted at rest using Fernet symmetric encryption.
          Passwords are hashed with bcrypt. Portal sessions use JWT with HttpOnly cookies over
          HTTPS. Sensitive financial data (card, ACH, SSN, government ID) is handled entirely by
          Stripe &mdash; never stored on Elevare servers.
        </Section>

        <Section title="5. Retention">
          We retain business data for the life of the engagement plus 7 years for tax and audit
          purposes. You may request deletion of non-obligatory data at any time by emailing
          kalib@elevaresolutions.me; certain records (financial, transactional) must be retained per
          applicable law.
        </Section>

        <Section title="5A. Recurring Payment Authorization Records">
          When you sign a Recurring ACH Debit Authorization inside your Customer Portal, we record
          the name you typed as your electronic signature, the date and time you signed, the terms
          version you accepted, and the IP address and browser you signed from. We keep this record
          for as long as the authorization is active plus <strong>two (2) years after you revoke
          it</strong>, in order to comply with the E-Sign Act and NACHA operating rules. You may
          request a copy of your authorization record at any time by emailing kalib@elevaresolutions.me.
        </Section>

        <Section title="6. Your Rights">
          Depending on your jurisdiction you may have rights to access, correct, export, or delete
          your personal data. Contact us at kalib@elevaresolutions.me to exercise these rights. If you
          are in the EU/UK/California you have additional rights under GDPR / CCPA; we will respond
          within statutory windows.
        </Section>

        <Section title="7. Cookies">
          We use strictly-necessary cookies for authentication (portal session) and preferences
          (time-zone). We do not use tracking cookies for third-party advertising.
        </Section>

        <Section title="8. Children">
          The Services are not directed to individuals under 18. We do not knowingly collect data
          from children.
        </Section>

        <Section title="9. International Transfers">
          Data may be processed in the United States or other countries where our sub-processors
          operate. By using the Services you consent to such transfers.
        </Section>

        <Section title="10. Changes">
          We may update this Policy by posting a new version on this page. Continued use of the
          Services after the &ldquo;Updated&rdquo; date constitutes acceptance.
        </Section>

        <Section title="11. Contact">
          Elevare Solutions<br />
          Kalib Whaley &mdash; Founder &amp; CEO &mdash; <a className="text-[#a3823f] hover:underline" href="mailto:kalib@elevaresolutions.me">kalib@elevaresolutions.me</a><br />
          John Caron &mdash; Co-Founder &amp; COO &mdash; <a className="text-[#a3823f] hover:underline" href="mailto:john@elevaresolutions.me">john@elevaresolutions.me</a>
        </Section>

        <p className="mt-16 text-[10px] uppercase tracking-[0.22em] text-black/40">
          Last updated {updated}. See also the <Link className="text-[#a3823f] hover:underline" to="/terms">Terms of Service</Link>.
        </p>
      </main>

      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-12">
      <h2 className="font-head text-xl text-[#1e3d3a]">{title}</h2>
      <div className="mt-3 text-sm text-black/70 leading-relaxed">{children}</div>
    </section>
  );
}
